import { useUpdateTaskStartEnd } from "@entities/campaigns/lib/hooks";
import {
  ITaskStageStartEnd,
  IUpdateTaskStartEndPayload,
} from "@entities/campaigns/lib/types";
import { BriefCampaignInf } from "@shared/lib/types/briefCampains";
import {
  applyStageDurations,
  CampaignTimelineStage,
  DeliverableTimelineMap,
  GanttCreator,
  spreadStagesEvenly,
  startOfDay,
} from "@widgets/GanttChart";
import { useCallback, useEffect, useRef, useState } from "react";
import { toISOStringSafe } from "../ganttAdapter";

interface UseCampaignGanttTimelineProps {
  briefId?: string;
  allCreators: GanttCreator[];
  activeInfluencers: BriefCampaignInf[];
  selectedDeliverableId?: string | null;
}

export function useCampaignGanttTimeline({
  briefId,
  allCreators,
  activeInfluencers,
  selectedDeliverableId,
}: UseCampaignGanttTimelineProps) {
  const [dirtyDeliverableKeys, setDirtyDeliverableKeys] = useState<Set<string>>(
    () => new Set()
  );

  const [timelineByDeliverable, setTimelineByDeliverable] =
    useState<DeliverableTimelineMap>({});

  const timelineByDeliverableRef = useRef(timelineByDeliverable);
  timelineByDeliverableRef.current = timelineByDeliverable;

  const doneSuccessCallbackRef = useRef<(() => void) | null>(null);

  const allCreatorsRef = useRef(allCreators);
  allCreatorsRef.current = allCreators;

  const { updateTaskStartEnd, isUpdating } = useUpdateTaskStartEnd(
    briefId || "",
    () => {
      allCreatorsRef.current.forEach((creator) => {
        creator.deliverables.forEach((del) => {
          const key = `${creator.id}_${del.id}`;
          const currentTimeline =
            timelineByDeliverableRef.current[key] ||
            timelineByDeliverableRef.current[del.id];
          if (currentTimeline) {
            del.stages = currentTimeline.map((s) => ({ ...s }));
          }
        });
      });
      setDirtyDeliverableKeys(new Set());
      doneSuccessCallbackRef.current?.();
      doneSuccessCallbackRef.current = null;
    },
    () => {
      doneSuccessCallbackRef.current = null;
    }
  );

  const resetStagesToClean = useCallback(() => {
    setDirtyDeliverableKeys(new Set());
    const next: DeliverableTimelineMap = {};
    allCreatorsRef.current.forEach((creator) => {
      creator.deliverables.forEach((del) => {
        const freshStages = del.stages?.length
          ? del.stages.map((s) => ({ ...s }))
          : [];
        next[`${creator.id}_${del.id}`] = freshStages;
        next[del.id] = freshStages;
      });
    });
    setTimelineByDeliverable(next);
  }, []);

  const prevDeliverableIdRef = useRef(selectedDeliverableId);
  useEffect(() => {
    if (prevDeliverableIdRef.current !== selectedDeliverableId) {
      prevDeliverableIdRef.current = selectedDeliverableId;
      resetStagesToClean();
    }
  }, [selectedDeliverableId, resetStagesToClean]);

  useEffect(() => {
    if (!allCreators.length) return;
    setTimelineByDeliverable((prev) => {
      let hasChanged = false;
      const next = { ...prev };
      allCreators.forEach((creator) => {
        creator.deliverables.forEach((del) => {
          const key = `${creator.id}_${del.id}`;
          if (
            !dirtyDeliverableKeys.has(key) &&
            !dirtyDeliverableKeys.has(del.id)
          ) {
            const freshStages = del.stages?.length
              ? del.stages.map((s) => ({ ...s }))
              : [];

            const current = next[key];
            const isDifferent =
              !current ||
              current.length !== freshStages.length ||
              current.some(
                (cs, i) =>
                  cs.id !== freshStages[i]?.id ||
                  cs.startDate !== freshStages[i]?.startDate ||
                  cs.endDate !== freshStages[i]?.endDate ||
                  cs.status !== freshStages[i]?.status ||
                  cs.isCancelled !== freshStages[i]?.isCancelled
              );

            if (isDifferent) {
              next[key] = freshStages;
              next[del.id] = freshStages;
              hasChanged = true;
            }
          }
        });
      });
      return hasChanged ? next : prev;
    });
  }, [allCreators, dirtyDeliverableKeys]);

  const handleUpdateDeliverableTimeline = useCallback(
    (
      creatorId: string | number,
      deliverableId: string,
      next: CampaignTimelineStage[]
    ) => {
      const deliverableKey = `${creatorId}_${deliverableId}`;
      setDirtyDeliverableKeys((prev) => new Set(prev).add(deliverableKey));
      setTimelineByDeliverable((prev: DeliverableTimelineMap) => ({
        ...prev,
        [deliverableKey]: next,
        [deliverableId]: next,
      }));
    },
    []
  );

  const handleChangeDeliverableTimeline = useCallback(
    (deliverableKey: string, next: CampaignTimelineStage[]) => {
      setDirtyDeliverableKeys((prev) => new Set(prev).add(deliverableKey));
      setTimelineByDeliverable((prev: DeliverableTimelineMap) => ({
        ...prev,
        [deliverableKey]: next,
      }));
    },
    []
  );

  const handleApplyTimelineToAll = useCallback(
    (next: CampaignTimelineStage[]) => {
      setDirtyDeliverableKeys((prev) => {
        const updated = new Set(prev);
        allCreators.forEach((c) => {
          c.deliverables.forEach((d) => {
            updated.add(`${c.id}_${d.id}`);
            updated.add(d.id);
          });
        });
        return updated;
      });

      setTimelineByDeliverable((prev: DeliverableTimelineMap) => {
        const updated: DeliverableTimelineMap = { ...prev };
        allCreators.forEach((c) => {
          c.deliverables.forEach((d) => {
            const deliverableKey = `${c.id}_${d.id}`;
            const existingStages =
              prev[deliverableKey] ||
              prev[d.id] ||
              (d.stages && d.stages.length > 0
                ? d.stages
                : []);

            const staged = existingStages.map((s, idx) => {
              const matching =
                next.find((ns) => ns.id === s.id) ||
                next[idx] ||
                s;
              return {
                ...s,
                startDate: matching.startDate,
                endDate: matching.endDate,
              };
            });

            updated[deliverableKey] = staged;
            updated[d.id] = staged;
          });
        });
        return updated;
      });
    },
    [allCreators]
  );

  const handleApplyStageDurations = useCallback(
    (durations: Record<string, number>, startDate?: Date) => {
      const effectiveStart = startDate
        ? startOfDay(startDate)
        : startOfDay(new Date());

      setDirtyDeliverableKeys((prev) => {
        const updated = new Set(prev);
        allCreators.forEach((c) => {
          c.deliverables.forEach((d) => {
            updated.add(`${c.id}_${d.id}`);
            updated.add(d.id);
          });
        });
        return updated;
      });

      setTimelineByDeliverable((prev: DeliverableTimelineMap) => {
        const updated: DeliverableTimelineMap = { ...prev };
        allCreators.forEach((c) => {
          c.deliverables.forEach((d) => {
            const deliverableKey = `${c.id}_${d.id}`;
            const existingStages =
              prev[deliverableKey] ||
              prev[d.id] ||
              (d.stages && d.stages.length > 0
                ? d.stages
                : []);

            const staged = applyStageDurations(
              existingStages,
              effectiveStart,
              durations
            );
            updated[deliverableKey] = staged;
            updated[d.id] = staged;
          });
        });
        return updated;
      });

      // ToastService.success("Stage durations applied to all deliverables");
    },
    [allCreators]
  );

  const handleEqualizeAllTimelines = useCallback(
    (rangeStart: Date, rangeEnd: Date) => {
      if (rangeEnd < rangeStart) {
        return;
      }
      setDirtyDeliverableKeys((prev) => {
        const updated = new Set(prev);
        allCreators.forEach((c) => {
          c.deliverables.forEach((d) => {
            updated.add(`${c.id}_${d.id}`);
            updated.add(d.id);
          });
        });
        return updated;
      });

      setTimelineByDeliverable((prev: DeliverableTimelineMap) => {
        const updated: DeliverableTimelineMap = { ...prev };
        allCreators.forEach((c) => {
          c.deliverables.forEach((d) => {
            const deliverableKey = `${c.id}_${d.id}`;
            const existingStages =
              prev[deliverableKey] ||
              prev[d.id] ||
              (d.stages && d.stages.length > 0
                ? d.stages
                : []);

            const staged = spreadStagesEvenly(
              existingStages,
              rangeStart,
              rangeEnd
            );
            updated[deliverableKey] = staged;
            updated[d.id] = staged;
          });
        });
        return updated;
      });
    },
    [allCreators]
  );

  const handleDone = useCallback(
    (onSuccess?: () => void) => {
      const payload: IUpdateTaskStartEndPayload = {};

      activeInfluencers.forEach((inf) => {
        const creatorId = inf._id;
        if (!creatorId || !inf.deliverables) return;

        inf.deliverables.forEach((del) => {
          const deliverableId = del?._id;
          if (!deliverableId) return;

          const deliverableKey = `${creatorId}_${deliverableId}`;

          // Only include if this deliverable had updates or was touched
          if (
            dirtyDeliverableKeys.size > 0 &&
            !dirtyDeliverableKeys.has(deliverableKey) &&
            !dirtyDeliverableKeys.has(deliverableId)
          ) {
            return;
          }

          const stages =
            timelineByDeliverable[deliverableKey] ||
            timelineByDeliverable[deliverableId];

          if (!stages || stages.length === 0) return;

          const taskMap: Record<string, ITaskStageStartEnd> = {};

          if (del.tasks && del.tasks.length > 0) {
            del.tasks.forEach((task, taskIdx) => {
              const taskId = task?._id;
              if (!taskId) return;

              const matchingStage =
                stages.find(
                  (s) =>
                    s.taskId === taskId ||
                    s.id === taskId ||
                    s.id === (task.type || "").toLowerCase() ||
                    s.id === (task.taskName || "").toLowerCase()
                ) || stages[taskIdx];

              if (matchingStage?.startDate && matchingStage?.endDate) {
                const startAt = toISOStringSafe(matchingStage.startDate);
                const endAt = toISOStringSafe(matchingStage.endDate);
                if (startAt && endAt) {
                  taskMap[taskId] = {
                    startAt,
                    endAt,
                  };
                }
              }
            });
          } else {
            stages.forEach((stage) => {
              const taskId = stage.taskId || stage.id;
              if (stage.startDate && stage.endDate && taskId) {
                const startAt = toISOStringSafe(stage.startDate);
                const endAt = toISOStringSafe(stage.endDate);
                if (startAt && endAt) {
                  taskMap[taskId] = {
                    startAt,
                    endAt,
                  };
                }
              }
            });
          }

          if (Object.keys(taskMap).length > 0) {
            if (!payload[creatorId]) {
              payload[creatorId] = {};
            }
            payload[creatorId][deliverableId] = taskMap;
          }
        });
      });

      if (Object.keys(payload).length === 0) {
        onSuccess?.();
        return;
      }

      doneSuccessCallbackRef.current = onSuccess || null;
      updateTaskStartEnd(payload);
    },
    [
      activeInfluencers,
      dirtyDeliverableKeys,
      timelineByDeliverable,
      updateTaskStartEnd,
    ]
  );

  return {
    timelineByDeliverable,
    handleUpdateDeliverableTimeline,
    handleChangeDeliverableTimeline,
    handleApplyTimelineToAll,
    handleEqualizeAllTimelines,
    handleApplyStageDurations,
    handleDone,
    handleCancel: resetStagesToClean,
    isSaving: isUpdating,
  };
}
